import { type HTMLAttributes, isValidElement, type ReactNode } from "react";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Flex, theme, Tooltip } from "antd";

import { Typography } from "../atoms/typography/Typography";

import type { AvE2EProps } from "../types";
import type { AbstractTooltipProps, TooltipPlacement } from "antd/es/tooltip";

type TooltipLabelProps = AvE2EProps & {
  label: ReactNode;
  title: ReactNode;
  placement?: TooltipPlacement;
  icon?: ReactNode;
  tooltipColor?: AbstractTooltipProps["color"];
  /**
   * Truncates a long label with an ellipsis instead of wrapping across multiple lines. Use when
   * the label sits in a width-constrained container (the tooltip already surfaces the full text
   * on hover, so truncation loses nothing).
   */
  ellipsis?: boolean;
} & Omit<HTMLAttributes<HTMLDivElement>, "title">;

export function TooltipLabel({
  label,
  title,
  testIdPrefix,
  placement = "right",
  icon,
  tooltipColor,
  ellipsis,
  ...spanProps
}: TooltipLabelProps) {
  const { Text } = Typography;
  const { token } = theme.useToken();
  const resolvedIcon = icon ?? <InfoCircleOutlined style={{ color: token.colorPrimary }} />;

  const isHtmlElement = isValidElement(title) && typeof title.type === "string";
  const tooltipTitle =
    tooltipColor === "white" && !isHtmlElement ? (
      <span style={{ color: token.colorText }}>{title}</span>
    ) : (
      title
    );

  return (
    <div
      {...spanProps}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        ...(ellipsis ? { minWidth: 0, width: "100%" } : {}),
        ...(spanProps.style || {}),
      }}
    >
      <Text
        testIdPrefix={`${testIdPrefix}-tooltip-label-label`}
        ellipsis={ellipsis}
        style={ellipsis ? { flex: 1, minWidth: 0 } : undefined}
      >
        {label}
      </Text>
      <Tooltip
        title={tooltipTitle}
        placement={placement}
        data-testid={`${testIdPrefix}-tooltip-label-tooltip`}
        color={tooltipColor}
      >
        <Flex
          tabIndex={0}
          data-testid={`${testIdPrefix}-tooltip-label-icon`}
          style={{ display: "inline-flex", lineHeight: 0 }}
        >
          {resolvedIcon}
        </Flex>
      </Tooltip>
    </div>
  );
}
